import { useMemo, useState } from "react";
import { FormHelperText, Typography } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { VNominationDocumentTypes, type VNominationDocumentType } from "@constants/certificates";
import { useNotification } from "@context/NotificationContext";
import { getCurrentUaeDate } from "@utils/date-time";
import { t } from "i18next";
import { useOutletContext } from "react-router-dom";
import { generateCertificateField } from "../../../utils";
import type { VesselNominationOutletContext } from "../../constants";
import { useUploadDocument } from "../../hooks/useUploadDocument";

const emptyArray: never[] = [];
const LANG_BASE = "vessel-nomination";

type DocumentProps = {
  documentType: VNominationDocumentType;
  label: string;
};

type DocumentCategoryValue =
  | "CERTIFICATE"
  | "PERMIT"
  | "DOCUMENT"
  | "INSURANCE"
  | "VESSEL_MASTER_SOF"
  | "OTHERS_SPECIFY";

const documentTypeOptions = [
  { label: "Certificate", value: "CERTIFICATE" },
  { label: "Permit", value: "PERMIT" },
  { label: "Document", value: "DOCUMENT" },
  { label: "Insurance", value: "INSURANCE" },
  { label: "Vessel Master-SoF", value: "VESSEL_MASTER_SOF" },
  { label: "Others-Specify", value: "OTHERS_SPECIFY" },
];

const getDefaultDocumentCategory = (
  documentType: VNominationDocumentType,
): DocumentCategoryValue | "" => {
  switch (documentType) {
    case VNominationDocumentTypes.NOR:
    case VNominationDocumentTypes.MASTER_COMPLIANCE:
    case VNominationDocumentTypes.STOWAGE_PLAN:
    case VNominationDocumentTypes.LOADING_PLAN:
    case VNominationDocumentTypes.DISCHARGE_PLAN:
    case VNominationDocumentTypes.H2S_LOG:
    case VNominationDocumentTypes.TEMPERATURE_LOG:
      return "DOCUMENT";

    case VNominationDocumentTypes.LAST_PORT_CLEARANCE:
    case VNominationDocumentTypes.NOC_HARBOUR_MASTER:
      return "PERMIT";

    default:
      return "";
  }
};

const DocumentCard = ({ documentType, label }: DocumentProps) => {
  const { formData, updateFormData } = useOutletContext<VesselNominationOutletContext>();
  const { notify } = useNotification();
  const { certificates: uploadConditions } = useFileUploadProfiles();
  const [error, setError] = useState("");

  const fieldKey = `vessel-nomination-${documentType}`;

  const urlField = generateCertificateField.fileUrl(documentType);
  const documentNameField = generateCertificateField.documentName(documentType);
  const documentTypeField = generateCertificateField.documentType(documentType);
  const otherSpecifyField = generateCertificateField.otherSpecify(documentType);
  const expiryDateField = generateCertificateField.expiryDate(documentType);
  const issueDateField = generateCertificateField.issueDate(documentType);
  const norTimeField = generateCertificateField.norTime(documentType);

  const uploadedFiles = useUploadStore((state) => state.filesByKey[fieldKey] ?? emptyArray);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);

  const selectedDocumentType = formData.watch(documentTypeField);
  const documentUrlError = formData.getFieldState(urlField, formData.formState).error?.message;

  const hasUploadedFile = !!uploadedFiles[0];

  const showNorTime = documentType === VNominationDocumentTypes.NOR;

  const showIssueDate = useMemo(() => {
    const issueDateDocumentTypes: VNominationDocumentType[] = [
      VNominationDocumentTypes.MASTER_COMPLIANCE,
      VNominationDocumentTypes.STOWAGE_PLAN,
      VNominationDocumentTypes.LOADING_PLAN,
      VNominationDocumentTypes.DISCHARGE_PLAN,
      VNominationDocumentTypes.H2S_LOG,
      VNominationDocumentTypes.TEMPERATURE_LOG,
      VNominationDocumentTypes.LAST_PORT_CLEARANCE,
      VNominationDocumentTypes.NOC_HARBOUR_MASTER,
    ];
    return issueDateDocumentTypes.includes(documentType);
  }, [documentType]);

  const showExpiryDate = useMemo(
    () => documentType === VNominationDocumentTypes.NOC_HARBOUR_MASTER,
    [documentType],
  );

  const issueDateRequired = useMemo(() => {
    const issueDateRequiredDocumentTypes: VNominationDocumentType[] = [
      VNominationDocumentTypes.MASTER_COMPLIANCE,
      VNominationDocumentTypes.LAST_PORT_CLEARANCE,
      VNominationDocumentTypes.NOC_HARBOUR_MASTER,
    ];

    return issueDateRequiredDocumentTypes.includes(documentType);
  }, [documentType]);

  const expiryDateRequired = false;

  const showOtherSpecify = selectedDocumentType === "OTHERS_SPECIFY";

  const visibleColumns =
    1 +
    (showOtherSpecify ? 1 : 0) +
    (showNorTime ? 1 : 0) +
    (showIssueDate ? 1 : 0) +
    (showExpiryDate ? 1 : 0);

  const gridColsClass =
    visibleColumns === 5
      ? "lg:grid-cols-5"
      : visibleColumns === 4
        ? "lg:grid-cols-4"
        : visibleColumns === 3
          ? "lg:grid-cols-3"
          : "lg:grid-cols-2";
  const handleUploadSuccess = ({
    documentUrl,
    documentName,
  }: {
    documentUrl: string;
    documentType: string;
    documentName: string;
  }) => {
    const now = getCurrentUaeDate();
    const defaultDocumentCategory = getDefaultDocumentCategory(documentType);

    formData.setValue(urlField, documentUrl, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });

    formData.setValue(documentNameField, documentName, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });

    formData.setValue(documentTypeField, defaultDocumentCategory, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });

    if (defaultDocumentCategory !== "OTHERS_SPECIFY") {
      formData.setValue(otherSpecifyField, "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData(otherSpecifyField, "");
    }

    if (showNorTime) {
      formData.setValue(norTimeField, now, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (showIssueDate) {
      formData.setValue(issueDateField, null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData(issueDateField, null);
    }

    if (showExpiryDate) {
      formData.setValue(expiryDateField, null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData(expiryDateField, null);
    }

    updateFormData(urlField, documentUrl);
    updateFormData(documentNameField, documentName);
    updateFormData(documentTypeField, defaultDocumentCategory);
    updateFormData(norTimeField, showNorTime ? now : null);
    setFilesForKey(fieldKey, [{ name: documentName || documentType, url: documentUrl }]);
  };

  const handleUploadError = () => {
    setFilesForKey(fieldKey, []);

    formData.setValue(urlField, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    formData.setValue(documentNameField, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });

    updateFormData(urlField, "");
    updateFormData(documentNameField, "");

    notify({
      title: t("vessel-nomination.notifications.upload-error-title"),
      description: t("vessel-nomination.notifications.upload-error-description"),
      type: "error",
    });
  };

  const uploadDocument = useUploadDocument({
    handleUploadSuccess,
    handleUploadError,
  });

  const handleUpload = (incomingFiles: File[]) => {
    setError("");
    const file = incomingFiles?.[0];
    if (!file) return;

    uploadDocument.mutate({
      file,
      documentType,
    });
  };

  const handleReject = () => {
    const typesLabel = uploadConditions.format
      ?.map((type) => type.toUpperCase().replace(".", ""))
      .join(", ");

    setError(
      t("form-validation.certificate-size-and-type", {
        max: uploadConditions.maxSizeLabel,
        types: typesLabel,
      }),
    );
  };

  const handleRemoveFile = () => {
    formData.setValue(urlField, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    formData.setValue(documentTypeField, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    formData.setValue(documentNameField, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    formData.setValue(otherSpecifyField, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    if (showIssueDate) {
      formData.setValue(issueDateField, null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    }

    if (showExpiryDate) {
      formData.setValue(expiryDateField, null, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    }

    updateFormData(urlField, "");
    updateFormData(documentNameField, "");
    updateFormData(documentTypeField, "");
    updateFormData(otherSpecifyField, "");

    if (showIssueDate) {
      updateFormData(issueDateField, null);
    }

    if (showExpiryDate) {
      updateFormData(expiryDateField, null);
    }

    setFilesForKey(fieldKey, []);
  };

  const allowedExtensions = (uploadConditions.format ?? []).map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  return (
    <div className="rounded-sm border border-[#E6EDF2] relative">
      <div className="flex items-center bg-[#F7F8FA] px-8 min-h-12">
        <Typography variant="body2">{t(label)}</Typography>
      </div>

      <div className="flex py-4 px-8 gap-4 flex-col">
        {!hasUploadedFile && (
          <div className={uploadDocument.isPending ? "opacity-50" : ""}>
            <FileUpload
              testId={`${documentType}-upload`}
              onUpload={handleUpload}
              onReject={handleReject}
              name={fieldKey}
              conditions={uploadConditions}
              files={uploadedFiles}
              setFiles={(next) => setFilesForKey(fieldKey, next.slice(0, 1))}
              maxFiles={1}
              allowedExtensions={allowedExtensions}
              disabled={uploadDocument.isPending}
              uploadDescription={t("upload.additional-documents-upload", {
                extensions: uploadConditions.format.join(", "),
                size: uploadConditions.maxSizeLabel,
              })}
              isError={!!error || !!documentUrlError}
            />

            {(error || documentUrlError) && (
              <FormHelperText sx={{ ml: 0, color: "error.main" }}>
                {error || documentUrlError}
              </FormHelperText>
            )}
          </div>
        )}

        {hasUploadedFile && (
          <>
            <SingleFile
              testId={`${documentType}-file`}
              file={uploadedFiles[0]}
              fieldName={fieldKey}
              currentFileIndex={0}
              setFiles={handleRemoveFile}
              isLoading={uploadDocument.isPending}
            />

            <div className={`grid w-full gap-4 grid-cols-1 ${gridColsClass}`}>
              <FormItem
                testId={`${documentType}-document-type`}
                name={documentTypeField}
                type="select"
                label={t(`${LANG_BASE}.add.form.fields.document-type`)}
                options={documentTypeOptions}
                onFieldChange={updateFormData}
                disabled
              />

              {showOtherSpecify && (
                <FormItem
                  testId={`${documentType}-other-specify`}
                  name={otherSpecifyField}
                  type="text"
                  maxChars={30}
                  label={t(`${LANG_BASE}.add.form.fields.other-specify`)}
                  onFieldChange={updateFormData}
                />
              )}

              {showNorTime && (
                <FormItem
                  testId={`${documentType}-nor-time`}
                  name={norTimeField}
                  type="date-picker"
                  label={t(`${LANG_BASE}.add.form.fields.nor-time`)}
                />
              )}

              {showIssueDate && (
                <FormItem
                  testId={`${documentType}-issue-date`}
                  name={issueDateField}
                  type="date-picker"
                  label={t(`${LANG_BASE}.add.form.fields.issue-date`)}
                  onFieldChange={updateFormData}
                  timePicker={false}
                  dateConstraint="not-future"
                  required={issueDateRequired}
                />
              )}

              {showExpiryDate && (
                <FormItem
                  testId={`${documentType}-expiry-date`}
                  name={expiryDateField}
                  type="date-picker"
                  label={t(`${LANG_BASE}.add.form.fields.expiration-date`)}
                  onFieldChange={updateFormData}
                  timePicker={false}
                  dateConstraint="not-past"
                  required={expiryDateRequired}
                />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentCard;
